class GoogleAuthService {
  constructor() {
    this.isInitialized = false;
    this.initPromise = null;
  }

  // Initialize Google Sign-In
  async initialize() {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      // Load Google Sign-In script
      if (!window.google) {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.initializeGoogleSignIn().then(resolve).catch(reject);
        };
        script.onerror = () =>
          reject(new Error("Failed to load Google Sign-In script"));
        document.head.appendChild(script);
      } else {
        this.initializeGoogleSignIn().then(resolve).catch(reject);
      }
    });

    return this.initPromise;
  }

  async initializeGoogleSignIn() {
    try {
      // Initialize Google Sign-In with your client ID
      window.google.accounts.id.initialize({
        client_id:
          process.env.REACT_APP_GOOGLE_CLIENT_ID ||
          "your-google-client-id.googleusercontent.com",
        callback: this.handleCredentialResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      this.isInitialized = true;
      console.log("Google Sign-In initialized successfully");
    } catch (error) {
      console.error("Failed to initialize Google Sign-In:", error);
      throw error;
    }
  }

  // Handle Google credential response
  handleCredentialResponse(response) {
    // This will be overridden by the component
    console.log("Google credential response:", response);
  }

  // Decode JWT token to get user info
  decodeJWT(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Failed to decode JWT:", error);
      throw new Error("Invalid token format");
    }
  }

  // Trigger Google Sign-In popup
  async signIn() {
    try {
      await this.initialize();
      return new Promise((resolve, reject) => {
        // Set up callback for this specific sign-in attempt
        this.handleCredentialResponse = (response) => {
          try {
            const userInfo = this.decodeJWT(response.credential);
            resolve({
              credential: response.credential,
              userInfo: {
                fullName: userInfo.name,
                email: userInfo.email,
                picture: userInfo.picture,
                emailVerified: userInfo.email_verified,
                sub: userInfo.sub,
              },
            });
          } catch (error) {
            reject(error);
          }
        };

        // Trigger the sign-in prompt
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            // Fallback to popup if prompt is not displayed
            window.google.accounts.id.renderButton(
              document.createElement("div"),
              {
                theme: "outline",
                size: "large",
                width: "100%",
              }
            );

            // Create a temporary button and click it
            const tempButton = document.createElement("div");
            tempButton.style.display = "none";
            document.body.appendChild(tempButton);

            window.google.accounts.id.renderButton(tempButton, {
              theme: "outline",
              size: "large",
              click_listener: () => {
                // This will trigger the popup
              },
            });

            // Clean up
            setTimeout(() => {
              if (tempButton.parentNode) {
                tempButton.parentNode.removeChild(tempButton);
              }
            }, 100);
          }
        });
      });
    } catch (error) {
      console.error("Google Sign-In failed:", error);
      throw error;
    }
  }

  // Sign out
  signOut() {
    if (window.google && window.google.accounts) {
      window.google.accounts.id.disableAutoSelect();
    }
  }
}

const googleAuthService = new GoogleAuthService();
export default googleAuthService;
